import { Button } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router';

import { captureAndroidSsoRequest } from '../android-bridge';
import { homePathFor, useAuthStore } from '../auth-store';
import { isDevLoginAvailable } from '../auth-api';
import { emsAuth, isSsoConfigured } from '../ems';
import { FullPageSpinner } from '../../components/FullPageSpinner';
import { AuthCard } from './AuthCard';

/**
 * `/login` — the SSO entry point.
 *
 * `auth.login()` is a full-page redirect to EMS, which comes back to
 * `/auth/callback` (ARCHITECTURE §5). Nothing is stored here; the deep link the
 * user was after rides along in router state.
 *
 * The dev-login link only appears when the server actually has DEV_AUTH_BYPASS
 * on — probed, not guessed (see `isDevLoginAvailable`). In production the probe
 * returns 404 and the link never renders.
 */
export function LoginPage(): ReactNode {
  const { t } = useTranslation('shell');
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);

  const devLogin = useQuery({
    queryKey: ['auth', 'dev-login-available'],
    queryFn: ({ signal }) => isDevLoginAvailable(signal),
    staleTime: Infinity,
    retry: false,
  });

  // Android scanner SSO bridge: `?source=android&return=<app scheme>` means a
  // Custom Tab opened us purely to run the EMS round trip — capture the
  // (allow-listed) return URI and go straight to EMS without a click. Lazy
  // useState so the capture happens exactly once, BEFORE the authenticated
  // branch below can Navigate away (the Custom Tab shares Chrome's cookies, so
  // a live web session must not swallow the app's sign-in). android-bridge.ts.
  const [androidSso] = useState(() => captureAndroidSsoRequest(location.search));
  useEffect(() => {
    if (androidSso && isSsoConfigured()) {
      setRedirecting(true);
      // Full-page navigation to EMS; comes back to /auth/callback which
      // forwards the token to the app scheme and never touches web session.
      emsAuth().login();
    }
  }, [androidSso]);

  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'authenticated' && !androidSso) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homePathFor(user)} replace />;
  }

  const ssoConfigured = isSsoConfigured();

  return (
    <AuthCard
      title={t('auth.signInTitle')}
      subtitle={ssoConfigured ? t('auth.signInSubtitle') : t('auth.ssoNotConfigured')}
      footer={
        devLogin.data ? (
          <Link to="/dev-login" className="underline underline-offset-4 hover:text-ink">
            {t('auth.devLogin')}
          </Link>
        ) : null
      }
    >
      <Button
        className="w-full"
        icon={<LogIn />}
        disabled={!ssoConfigured}
        loading={redirecting}
        onClick={() => {
          setRedirecting(true);
          // Full-page navigation — nothing after this runs.
          emsAuth().login();
        }}
      >
        {redirecting ? t('auth.signingIn') : t('auth.signInWithEms')}
      </Button>
    </AuthCard>
  );
}
